import { formatDateForAPI } from "utils/utils";
import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export default function useTableFilters({
  filters,
  setFilters,
  initialFilters,
  setIsOpen,
  clearFilters,
  setSearch,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessedUrlParams = useRef(false);

  const [localFilters, setLocalFilters] = useState(() => {
    return Object.keys(filters).length > 0 ? filters : initialFilters;
  });

  useEffect(() => {
    if (hasProcessedUrlParams.current) return;

    const urlSearch = searchParams.get("search");
    const urlFilters = searchParams.get("filters");

    let shouldApplyFilters = false;
    let newFilters = { ...initialFilters };
    let newSearch = "";

    if (urlSearch) {
      newSearch = urlSearch;
      if (setSearch) {
        setSearch(urlSearch);
      }

      newFilters = { ...initialFilters };
      shouldApplyFilters = true;
    }

    if (urlFilters) {
      try {
        console.log("url filters", urlFilters);

        const parsedFilters = JSON.parse(decodeURIComponent(urlFilters));

        console.log("parsed filters", parsedFilters);

        newFilters = { ...initialFilters, ...parsedFilters };
        console.log("new filters", newFilters);

        shouldApplyFilters = true;

        if (!urlSearch && setSearch) {
          setSearch("");
        }
      } catch (error) {
        console.error("Error parsing filters from URL:", error);
      }
    }

    if (shouldApplyFilters) {
      console.log("should apply filters", shouldApplyFilters, newFilters);
      setLocalFilters(newFilters);
      setFilters(newFilters);
    }

    if (urlSearch || urlFilters) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("search");
      newSearchParams.delete("filters");

      const newUrl = newSearchParams.toString()
        ? `${location.pathname}?${newSearchParams.toString()}`
        : location.pathname;

      navigate(newUrl, { replace: true });
    }

    hasProcessedUrlParams.current = true;
  }, [
    searchParams,
    setSearch,
    setFilters,
    initialFilters,
    navigate,
    location.pathname,
  ]);

  const updateDateRangeFilter = (dateRange) => {
    setLocalFilters((prev) => {
      const newFilters = { ...prev };

      if (dateRange?.from || dateRange?.to) {
        newFilters.date = {
          _gte: formatDateForAPI(dateRange?.from),
          _lte: formatDateForAPI(dateRange?.to),
        };
      } else {
        delete newFilters.date;
      }

      return newFilters;
    });
  };

  const clearLocalFilters = useCallback(() => {
    setIsOpen(false);
    clearFilters();
    setLocalFilters(initialFilters || {});
  }, [clearFilters, initialFilters, setIsOpen]);

  const updateComboboxFilter = (key, selectedItem) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: selectedItem
        ? {
            // Store the complete item with "_" prefix so it won't be sent to API
            __selectedItem: selectedItem,
            // Keep the filter structure for API
            id: { _eq: selectedItem.value },
          }
        : undefined,
    }));
  };

  function handleSetLocalFilters(filter) {
    setLocalFilters((prev) => ({
      ...prev,
      ...filter,
    }));
  }

  const applyFilters = () => {
    setIsOpen(false);
    setFilters(localFilters);
  };

  return {
    localFilters,
    setLocalFilters,
    updateDateRangeFilter,
    updateComboboxFilter,
    clearLocalFilters,
    applyFilters,
    handleSetLocalFilters,
  };
}
